import * as React from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const cardVariants = cva("flex flex-col bg-card text-card-foreground rounded-card", {
  variants: {
    variant: {
      default: "border border-border max-md:border-transparent max-md:[[data-slot=card]_&]:border-border",
      highlighted: "border border-action",
      plain: "",
      transparent: "bg-transparent",
    },
    padding: {
      none: "gap-0 p-0",
      compact: "gap-3 p-3",
      default: "gap-4 p-4",
      roomy: "gap-6 p-6 max-md:gap-3 max-md:p-4",
    },
    bleed: {
      true: "max-md:-mx-3 max-md:rounded-none max-md:border-x-0 max-md:[&_[data-slot=card]]:rounded-none max-md:[&_[data-slot=card]]:border-0 max-md:[&_[data-slot=card]]:bg-transparent",
      false: "",
    },
  },
  defaultVariants: {
    variant: "default",
    padding: "default",
    bleed: false,
  },
});

function Card({
  className,
  variant,
  padding,
  bleed,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof cardVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? SlotPrimitive.Slot : "div";

  return (
    <Comp
      data-slot="card"
      className={cn(cardVariants({ variant, padding, bleed }), className)}
      {...props}
    />
  );
}

function CardHeader({
  className,
  separated = false,
  ...props
}: React.ComponentProps<"div"> & { separated?: boolean }) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 has-data-[slot=card-action]:grid-cols-[1fr_auto]",
        separated && "border-b border-border pb-4",
        className,
      )}
      {...props}
    />
  );
}

const cardTitleVariants = cva("tracking-[normal]", {
  variants: {
    size: {
      sm: "text-body font-semibold",
      default: "text-title",
      lg: "text-h3",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

function CardTitle({
  className,
  size,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardTitleVariants>) {
  return (
    <div
      data-slot="card-title"
      className={cn(cardTitleVariants({ size }), className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-body text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn(className)} {...props} />;
}

function CardFooter({
  className,
  separated = false,
  ...props
}: React.ComponentProps<"div"> & { separated?: boolean }) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center",
        separated && "border-t border-border pt-4",
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
